import { useHarnessCapabilities } from "@/api/harnessCapabilities";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  defaultModelFor,
  harnessIcon,
  harnessLabel,
  harnessSeatVisible,
  modelFundingLabel,
} from "@/constants/harnesses";
import type { AutomationHarnessSelection } from "@/panes/automation/automationHarness";
import { Flags } from "@/providers/LaunchDarklyProvider";
import {
  HarnessCapability,
  HarnessCapabilityModel,
  HarnessId,
} from "@cohesive/api-client";
import { useFlags } from "launchdarkly-react-client-sdk";

/** Stands in for "no pinned model" — a Select item's value cannot be empty. */
const HARNESS_DEFAULT_MODEL = "__harness_default__";

/** One row: name, and for a harness the user can't pick, the server's reason underneath. */
function HarnessOption({ capability }: { capability: HarnessCapability }) {
  const Icon = harnessIcon(capability.id);
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="flex min-w-0 items-center gap-2">
        {Icon && <Icon className="size-3.5 shrink-0 text-n-50" />}
        <span className="truncate">
          {harnessLabel(capability.id, capability.label)}
        </span>
      </span>
      {!capability.available && capability.unavailable_reason && (
        <span className="text-xs leading-snug text-muted-foreground">
          {capability.unavailable_reason}
        </span>
      )}
    </span>
  );
}

/** One row: the model, and what a run on it is paid with. */
function ModelOption({
  model,
  prefix,
}: {
  model: HarnessCapabilityModel;
  prefix?: string;
}) {
  const funding = modelFundingLabel(model.funding);
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="truncate">
        {prefix ? `${prefix} (${model.label})` : model.label}
      </span>
      {funding && (
        <span className="text-xs text-muted-foreground">{funding}</span>
      )}
    </span>
  );
}

/**
 * Whether the harness row belongs on the form at all. Gated the way the composer's selector is:
 * with the runtime off cohesive is the only choice and a one-option select is noise. An
 * automation already on a harness states it either way.
 *
 * Its own hook because the label is the caller's — the two surfaces use different label
 * primitives — and it is the caller's gate that decides: the fields render whatever they are
 * given, so a caller that skipped it would show an orphan label over a one-option select.
 */
export function useAutomationHarnessVisible(
  selection: AutomationHarnessSelection,
): boolean {
  const flags = useFlags<Flags>();
  const { data: capabilities } = useHarnessCapabilities();
  return harnessSeatVisible(
    selection.harness,
    !!flags.harnessRuntime,
    capabilities,
  );
}

/**
 * What a schedule's runs are stamped with. Two plain selects rather than the composer's pill:
 * this is a settings form, and the harness sits alongside Location and Schedule as one more row.
 *
 * A schedule's harness is changeable — the stamp lands on each run, not on the schedule — so
 * unlike a chat there is nothing to lock. A harness or model the catalog no longer offers (the
 * flag withdrawn, a model retired) is still listed while it is the one selected — the server
 * revalidates the stored pair on every PATCH, so a retired pin fails every edit, even a rename,
 * until it is replaced. A value the form hid would be one the user could not find to replace.
 */
export function AutomationHarnessFields({
  value,
  onChange,
  disabled,
}: {
  value: AutomationHarnessSelection;
  onChange: (next: AutomationHarnessSelection) => void;
  disabled?: boolean;
}) {
  const { data: capabilities } = useHarnessCapabilities();

  const catalog = capabilities ?? [];
  const capability = catalog.find((c) => c.id === value.harness);
  const options: HarnessCapability[] = capability
    ? catalog
    : [
        ...catalog,
        {
          id: value.harness,
          label: harnessLabel(value.harness),
          available: true,
          models: [],
        },
      ];
  const catalogModels = capability?.models ?? [];
  // A pin the catalog no longer lists is listed anyway, for the same reason the harness is: a
  // controlled select whose value has no row goes blank, which reads as unset on an automation
  // that is still pinned.
  const models =
    value.model && !catalogModels.some((m) => m.id === value.model)
      ? [...catalogModels, { id: value.model, label: value.model }]
      : catalogModels;
  const defaultModel = catalogModels.find(
    (m) => m.id === defaultModelFor(capability),
  );

  return (
    <div className="flex flex-col gap-2">
      <Select
        value={value.harness}
        disabled={disabled}
        onValueChange={(next) => {
          // No model, so the automation tracks whatever the harness defaults to. A schedule
          // provisions a fresh sandbox per run and outlives the model list it was made under.
          onChange({ harness: next as HarnessId });
        }}
      >
        <SelectTrigger className="h-9" aria-label="Runs on">
          <SelectValue placeholder="Runs on" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem
              key={option.id}
              value={option.id}
              disabled={!option.available}
            >
              <HarnessOption capability={option} />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Cohesive's dial is the reasoning tier, not a model, so it offers none. */}
      {models.length > 0 && (
        <Select
          value={value.model ?? HARNESS_DEFAULT_MODEL}
          disabled={disabled}
          onValueChange={(next) =>
            onChange({
              harness: value.harness,
              model: next === HARNESS_DEFAULT_MODEL ? undefined : next,
            })
          }
        >
          <SelectTrigger className="h-9" aria-label="Model">
            <SelectValue placeholder="Model" />
          </SelectTrigger>
          <SelectContent>
            {/* First and preselected: an automation left alone follows the platform default
                rather than pinning whichever model that resolved to the day it was made. */}
            <SelectItem value={HARNESS_DEFAULT_MODEL}>
              {defaultModel ? (
                <ModelOption model={defaultModel} prefix="Default" />
              ) : (
                "Default"
              )}
            </SelectItem>
            {models.map((model) => (
              <SelectItem key={model.id} value={model.id}>
                <ModelOption model={model} />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
